// ** Redux Imports
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"

// ** Axios Imports
import {
  addSocialMediaApi,
  getSocialMediaApi,
  getSocialMediaByIdApi,
  updateSocialMediaApi,
  deleteSocialMediaApi,
  updateSocialMediaStatusApi
} from "../../../../services/Web/socialMedia.service"
import { toastError, toastSuccess } from "../../../../utility/toastutill"

// ** Get All SocialMedia Data
export const getAllSocialMediaData = createAsyncThunk(
  "socialMedias/getAllSocialMediaData",
  async () => {
    try {
      const response = await getSocialMediaApi()
      if (response.data.success) {
        // toastSuccess(response.data.message)
      }
      return response.data.data
    } catch (error) {
      toastError(error)
      return error
    }
  } 
)

// ** Get SocialMedia with Pagination and Filtering
export const getSocialMediaData = createAsyncThunk(
  "socialMedias/getSocialMediasData",
  async (params) => {
    let query = ``
    if (params) {
      if (params.sort) {
        query = `sort=${params.sort}`
      }
      if (params.sortColumn) {
        query = `${query}&sortColumn=${params.sortColumn}`
      }
      if (params.q) {
        query = `${query}&q=${params.q}`
      }
      if (params.perPage) {
        query = `${query}&perPage=${params.perPage}`
      }
      if (params.page) {
        query = `${query}&page=${params.page}`
      }
      if (params.status) {
        query = `${query}&status=${params.status}`
      }
    }

    try {
      const response = await getSocialMediaApi(query)
      return {
        params,
        data: response.data.data,
        totalPages: response.data.count
      }
    } catch (error) {
      toastError(error)
      return error
    }
  }
)

// ** Get Single SocialMedia by ID
export const getSocialMedia = createAsyncThunk("socialMedias/getSocialMedia", async (id) => {
  try {
    const response = await getSocialMediaByIdApi(id)
    toastSuccess(response.data.message)
    return response.data.data
  } catch (error) {
    toastError(error)
    return error
  }
})

// ** Add New SocialMedia
export const addSocialMedia = createAsyncThunk(
  "socialMedias/addSocialMedia",
  async (formData, { dispatch, getState }) => {
    try {
      const res = await addSocialMediaApi(formData)
      if (res.data) {
        toastSuccess(res.data.message)
        await dispatch(getSocialMediaData(getState().socialMedias.params))
        await dispatch(getAllSocialMediaData())
      }
      return res.data.success ? res.data.success : false
    } catch (error) {
      toastError(error)
      return error
    }
  }
)

// ** Update SocialMedia
export const updateSocialMedia = createAsyncThunk(
  "socialMedias/updateSocialMedia",
  async (formData, { dispatch, getState }) => {
    try {
      const res = await updateSocialMediaApi(formData.id, formData)
      if (res.data.success) {
        // toastSuccess(res.data.message)
        await dispatch(getSocialMediaData(getState().socialMedias.params))
        await dispatch(getAllSocialMediaData())
      }
      return res.data.success ? res.data.success : false
    } catch (error) {
      toastError(error)
      return error
    }
  }
)

// ** Update SocialMedia Status
export const updateSocialMediaStatus = createAsyncThunk(
  "socialMedias/updateSocialMediaStatus",
  async (data, { dispatch, getState }) => {
    try {
      // data is expected to contain at least { id, ... } — adjust if your service expects different shape
      const res = await updateSocialMediaStatusApi(data.id, data.platform, data)
      if (res.data && res.data.success) {
        toastSuccess(res.data.message)
        await dispatch(getSocialMediaData(getState().socialMedias.params))
        await dispatch(getAllSocialMediaData())
      }
      return res.data.success ? res.data.success : false
    } catch (error) {
      toastError(error)
      return error
    }
  }
)

// ** Delete SocialMedia
export const deleteSocialMedia = createAsyncThunk(
  "socialMedias/deleteSocialMedia",
  async (id, { dispatch, getState }) => {
    try {
      const res = await deleteSocialMediaApi(id)
      if (res.data.success) {
        toastSuccess(res.data.message)
        await dispatch(getSocialMediaData(getState().socialMedias.params))
        await dispatch(getAllSocialMediaData())
      }
      return id
    } catch (error) {
      toastError(error)
      return error
    }
  }
)

export const socialMediasSlice = createSlice({
  name: "socialMedias",
  initialState: {
    data: [],
    total: 1,
    params: {},
    allData: [],
    selectedSocialMedia: null,
    success: false,
    loading: false,
    error: null
  },
  reducers: {
    clearSelectedSocialMedia: (state) => {
      state.selectedSocialMedia = null
    },
    clearError: (state) => {
      state.error = null
    }
  },
  extraReducers: (builder) => {
    builder
      // Get All SocialMedias Data
      .addCase(getAllSocialMediaData.fulfilled, (state, action) => {
        state.allData = action.payload
      })
      // Get SocialMedias with Params
      .addCase(getSocialMediaData.fulfilled, (state, action) => {
        state.data = action.payload.data
        state.params = action.payload.params
        state.total = action.payload.totalPages
        state.loading = false
      })
      .addCase(getSocialMediaData.pending, (state) => {
        state.loading = true
      })
      .addCase(getSocialMediaData.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Get Single SocialMedia
      .addCase(getSocialMedia.fulfilled, (state, action) => {
        state.selectedSocialMedia = action.payload
        state.loading = false
      })
      .addCase(getSocialMedia.pending, (state) => {
        state.loading = true
      })
      .addCase(getSocialMedia.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Add SocialMedia
      .addCase(addSocialMedia.fulfilled, (state, action) => {
        state.success = action.payload
        state.loading = false
      })
      .addCase(addSocialMedia.pending, (state) => {
        state.loading = true
      })
      .addCase(addSocialMedia.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Update SocialMedia
      .addCase(updateSocialMedia.fulfilled, (state, action) => {
        state.selectedSocialMedia = null
        state.success = action.payload
        state.loading = false
      })
      .addCase(updateSocialMedia.pending, (state) => {
        state.loading = true
      })
      .addCase(updateSocialMedia.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Update SocialMedia Status
      .addCase(updateSocialMediaStatus.fulfilled, (state, action) => {
        state.selectedSocialMedia = null
        state.success = action.payload
        state.loading = false
      })
      .addCase(updateSocialMediaStatus.pending, (state) => {
        state.loading = true
      })
      .addCase(updateSocialMediaStatus.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Delete SocialMedia
      .addCase(deleteSocialMedia.fulfilled, (state, action) => {
        state.loading = false
      })
      .addCase(deleteSocialMedia.pending, (state) => {
        state.loading = true
      })
      .addCase(deleteSocialMedia.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  }
})

export const { clearSelectedSocialMedia, clearError } = socialMediasSlice.actions

export default socialMediasSlice.reducer